import { Button, Checkbox, Stack, Typography } from '@mui/material';
import { AbsReactTable, ProgressBar, TABLE } from 'customer_portal-ui-shared';
import { getYear } from 'date-fns';
import { useEffect, useState } from 'react';
import { AbsColor } from '../../constants';
import { CalendarReportType } from '../../contracts/calendar-survey.contract';
import { IVessel } from '../../contracts/vessel.contract';
import { CalendarAction, CalendarFetch } from '../../features/calendar.feature';
import { VesselFetch } from '../../features/vessel.feature';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { Loading } from '../shared';
import CalendarListItem from './calendar-list-item.component';

const CalendarList = () => {
    const dispatch = useAppDispatch();

    const maxYear: number = 4;

    const vessels = useAppSelector((state) => state.Vessel.List.Vessels);
    const regulationFilter = useAppSelector((state) => state.Calendar.Filters.Regulations);
    const surveyFilter = useAppSelector((state) => state.Calendar.Filters.Surveys);
    const isLoadingVessel = useAppSelector((state) => state.Vessel.List.IsLoading);
    const isLoadingReport = useAppSelector((state) => state.Calendar.Report.IsLoading);
    const filteredVessels = useAppSelector((state) => state.Calendar.Filters.Vessels);
    const showGuidelines = useAppSelector((state) => state.ShowGuidelines.showGuideLines);

    const tableSize = [10, 20, 50];
    const [tableData, setTableData] = useState<any[]>([]);
    const [tablePage, setTablePage] = useState<any[]>([]);

    const [appliedFilters, setAppliedFilters] = useState<any>({
        searchType: 'list',
        resetPageIndex: false,
        resetFilters: true,
        filters: {},
        pageSize: tableSize[0],
        pageIndex: 0
    });

    const tableHeader = [
        {
            accessor: 'checkBox',
            Header: () => {
                return <Typography style={{ fontSize: '10px', color: 'red' }}></Typography>;
            },
            Cell: ({ row, cell }: any) => {
                if (!vessels || vessels.length === 0) return <></>;
                const classNumber = row.original.classNumber;
                const rowVessel = filteredVessels?.find((v) => v.ClassNumber === classNumber);

                return (
                    <Checkbox
                        checked={rowVessel ? true : false}
                        onClick={(e) => {
                            if (!vessels || vessels.length === 0) return <></>;

                            dispatch(CalendarAction.ClearFilterSurveys());

                            const classNumber = row.original.classNumber;
                            const rowVessel = vessels.find((v) => v.ClassNumber === classNumber);
                            if (!rowVessel) return;

                            // Check if the checkbox is checked or unchecked
                            const isChecked = filteredVessels?.find((f) => f.ClassNumber === classNumber);
                            if (isChecked) {
                                dispatch(CalendarAction.RemoveFilterVessel(rowVessel));
                                return;
                            }

                            dispatch(CalendarAction.AddFilterVessel([rowVessel]));
                        }}
                    />
                );
            }
        },
        {
            label: 'Vessel',
            accessor: 'vesselName',
            sorting: true,
            filtering: true
        },
        {
            label: 'IMO Number',
            accessor: 'imoNumber',
            sorting: true,
            filtering: true
        },
        {
            label: 'Class Number',
            accessor: 'classNumber',
            sorting: true,
            filtering: true
        },
        {
            label: getYear(new Date()),
            accessor: 'year1',
            Cell: ({ row, cell }: any) => <CalendarListItem index={row.index} year={getYear(new Date())} classNumber={row.original.classNumber} />
        },
        {
            label: getYear(new Date()) + 1,
            accessor: 'year2',
            Cell: ({ row, cell }: any) => <CalendarListItem index={row.index} year={getYear(new Date()) + 1} classNumber={row.original.classNumber} />
        },
        {
            label: getYear(new Date()) + 2,
            accessor: 'year3',
            Cell: ({ row, cell }: any) => <CalendarListItem index={row.index} year={getYear(new Date()) + 2} classNumber={row.original.classNumber} />
        },
        {
            label: getYear(new Date()) + 3,
            accessor: 'year4',
            Cell: ({ row, cell }: any) => <CalendarListItem index={row.index} year={getYear(new Date()) + 3} classNumber={row.original.classNumber} />
        },
        {
            label: getYear(new Date()) + 4,
            accessor: 'year5',
            Cell: ({ row, cell }: any) => <CalendarListItem index={row.index} year={getYear(new Date()) + 4} classNumber={row.original.classNumber} />
        }
    ];

    useEffect(() => {
        if (!vessels) {
            dispatch(VesselFetch.GetAll());
            return;
        }
        const sortedVessels = [...vessels].sort((a, b) => a.VesselName.localeCompare(b.VesselName));
        const data = sortedVessels.map((vessel, index) => {
            return {
                id: index,
                isSelected: false,
                vesselName: vessel.VesselName,
                imoNumber: vessel.ImoNumber,
                classNumber: vessel.ClassNumber
            };
        });
        setTableData(data);
    }, [vessels]);

    useEffect(() => {
        if (!surveyFilter || surveyFilter.length == 0 || !vessels || vessels.length == 0) return;

        // When a survey filter is applied, we need to filter the vessels based on the selected surveys
        dispatch(CalendarAction.ClearFilterVessels());

        // Get the unique class numbers from the survey filter
        const surveyClassNumber = Array.from(new Set(surveyFilter?.map((v) => v.ClassNumber)));

        // Filter the vessels based on the class numbers from the survey filter
        const vesselSelected = vessels.filter((v) => surveyClassNumber.includes(v.ClassNumber));
        dispatch(CalendarAction.AddFilterVessel(vesselSelected));
    }, [surveyFilter]);

    useEffect(() => {
        if (!tableData) return;
        populateTablePage(0, tableSize[0]);
    }, [tableData, regulationFilter]);

    const populateTablePage = (pageIndex: number, pageSize: number) => {
        if (!tableData || tableData.length === 0) return;

        const pageData = tableData.slice(pageIndex * pageSize, pageSize * (pageIndex + 1));
        pageData.forEach((item) => {
            dispatch(
                CalendarFetch.GetByClassNumberYear({
                    classNumber: item.classNumber,
                    maxYear: getYear(new Date()) + maxYear
                })
            );
        });
        setTablePage(pageData);
    };

    const applyFilter = (filtersObj: any) => {
        const { filters = {}, pageIndex: newPageIndex, pageSize: newPageSize, searchType: newSearchType } = filtersObj;
        const updatedFilters = {
            ...filtersObj
        };
        if (
            appliedFilters.searchType !== newSearchType ||
            appliedFilters.searchType === 'all' ||
            appliedFilters.pageSize !== newPageSize ||
            appliedFilters.pageIndex !== newPageIndex ||
            appliedFilters.resetFilters
        ) {
            searchVessels(filters, newSearchType, newPageIndex, newPageSize);
        }
        setAppliedFilters(updatedFilters);
    };

    const searchVessels = (filters: any, searchType: any, pageIndex: number, pageSize: number) => {
        const { searchTerm, columnId: string }: any = Object.values(filters)?.[0] || {};
        if (searchType === 'list') {
            populateTablePage(pageIndex, pageSize);
        } else if (searchType === 'all') {
            const globalRegex = new RegExp(searchTerm, 'gi');
            // TODO: Update to dynamically filter based on the columnId
            // const searchData = tableData?.filter((item: IPSCRow) => (searchTerm?.length > 0 ? globalRegex.test(item['VesselName']) : true)).slice(pageIndex * pageSize, pageSize * (pageIndex + 1));
        }
    };

    const downloadReport = (type: CalendarReportType, showGuidelines: boolean) => {
        let selectedVessels: IVessel[] = [];
        // If no vessels are selected, select all vessels
        if ((!filteredVessels || filteredVessels.length === 0) && vessels) {
            selectedVessels = vessels;
        } else if (filteredVessels && filteredVessels.length > 0) {
            selectedVessels = filteredVessels; // If vessels are selected, use the selected vessels
        }
        const imoNumbers = selectedVessels.map((v) => v.ImoNumber);
        dispatch(CalendarFetch.DownloadCalendarReport({ type: type, fileExtension: 'pdf', imoNumbers: imoNumbers, showGuidelines: showGuidelines }));
    };

    return (
        <Stack direction={'column'} style={{ width: '100%' }}>
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} style={{ height: '50px', width: '100%' }}>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-start'} gap={2} style={{ width: '50%' }}>
                    {isLoadingReport ? (
                        <Loading />
                    ) : (
                        <>
                            <Button variant="contained" onClick={() => downloadReport(CalendarReportType.DueDate, showGuidelines)} size="small">
                                PDF by Due Date
                            </Button>
                            <Button variant="contained" onClick={() => downloadReport(CalendarReportType.Vessel, showGuidelines)} size="small">
                                PDF by Vessel
                            </Button>
                            <Button variant="contained" onClick={() => downloadReport(CalendarReportType.Survey, showGuidelines)} size="small">
                                PDF by Survey
                            </Button>
                        </>
                    )}
                </Stack>
                <Stack direction={'row'} gap={2} alignItems={'center'} justifyContent={'flex-end'} style={{ width: '50%' }}>
                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={1}>
                        <div style={{ width: '25px' }}>
                            <ProgressBar value={100} color={AbsColor.HighRisk} />
                        </div>
                        <Typography style={{ fontWeight: 'lighter' }}>Past Due</Typography>
                    </Stack>

                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={1}>
                        <div style={{ width: '25px' }}>
                            <ProgressBar value={100} color={AbsColor.MediumRisk} />
                        </div>
                        <Typography style={{ fontWeight: 'lighter' }}>180 Days Due</Typography>
                    </Stack>

                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={1}>
                        <div style={{ width: '25px' }}>
                            <ProgressBar value={100} color={AbsColor.LowRisk} />
                        </div>
                        <Typography style={{ fontWeight: 'lighter' }}>180+ Days Due</Typography>
                    </Stack>
                </Stack>
            </Stack>

            <AbsReactTable
                shadow={false}
                tableBorder={true}
                loading={isLoadingVessel}
                tableType={TABLE.TYPE.BASIC}
                columns={tableHeader}
                data={tablePage}
                dataCount={tableData?.length}
                pagination={true}
                appliedFilters={appliedFilters}
                applyFilterCallback={applyFilter}
                pageOptions={tableSize}
                allDataProvided
            />
        </Stack>
    );
};

export default CalendarList;
